import React from "react";
import { Badge, Empty } from "antd";
import Chart from "react-apexcharts";
import {
  AiOutlineDollarCircle,
  AiOutlineExclamationCircle,
  AiOutlineFileDone,
  AiOutlinePercentage,
  AiOutlineTags,
  AiOutlineWallet,
} from "react-icons/ai";
import { Card, CardTitle, KpiStatTile, LinkRow, SectionShell } from "../blocks";
import {
  FEE_STATUS_META,
  INK,
  SECTION_COLORS,
  SERIES,
  areaBase,
  axisLabelStyle,
  baseChart,
  baseGrid,
  donutBase,
  formatCompactCurrency,
  formatCurrency,
} from "../chartTheme";

const ACCENT = SECTION_COLORS.finance;

const FinanceSection = ({ fee, overview, academicYear, onNavigate }) => {
  const feeStatusSeries = FEE_STATUS_META.map(
    (s) => fee.statusDistribution?.[s.key] || 0
  );
  const hasFeeStatusData = feeStatusSeries.some((v) => v > 0);

  const monthlyCollections = fee.monthlyCollections || [];
  const hasCollections = monthlyCollections.some((m) => m.amount > 0);

  const collectedPctOfExpected =
    fee.expected > 0 ? Math.round((fee.collected / fee.expected) * 100) : 0;

  const collectionGauge = {
    options: {
      chart: { ...baseChart("iskool360-collection-gauge", "radialBar") },
      colors: [SERIES[0]],
      plotOptions: {
        radialBar: {
          hollow: { size: "62%" },
          track: { background: INK.track },
          dataLabels: {
            name: { show: true, fontSize: "12px", color: INK.muted, offsetY: 20 },
            value: {
              show: true,
              fontSize: "26px",
              fontWeight: 700,
              color: INK.primary,
              offsetY: -16,
              formatter: (val) => `${val}%`,
            },
          },
        },
      },
      labels: ["Collection Rate"],
      stroke: { lineCap: "round" },
    },
    series: [fee.collectionRate || 0],
  };

  const collectionsArea = {
    options: {
      chart: baseChart("iskool360-monthly-collections", "area"),
      colors: [SERIES[0]],
      ...areaBase,
      xaxis: {
        categories: monthlyCollections.map((m) => m.month),
        axisBorder: { show: false },
        axisTicks: { show: false },
        labels: axisLabelStyle,
      },
      yaxis: { labels: { formatter: (v) => formatCompactCurrency(v) } },
      tooltip: {
        y: {
          formatter: (v, { dataPointIndex }) => {
            const m = monthlyCollections[dataPointIndex];
            const txns = m?.transactions || 0;
            return `${formatCurrency(v)} · ${txns} transaction${
              txns === 1 ? "" : "s"
            }`;
          },
        },
      },
    },
    series: [
      { name: "Collected", data: monthlyCollections.map((m) => m.amount) },
    ],
  };

  const feeStatusDonut = {
    options: {
      chart: baseChart("iskool360-fee-status", "donut"),
      labels: FEE_STATUS_META.map((s) => s.label),
      colors: FEE_STATUS_META.map((s) => s.color),
      ...donutBase("Student Fees"),
    },
    series: feeStatusSeries,
  };

  const totalTransactions = monthlyCollections.reduce(
    (sum, m) => sum + (m.transactions || 0),
    0
  );
  const avgTransactionValue =
    totalTransactions > 0 ? fee.collected / totalTransactions : 0;

  const transactionVolumeBar = {
    options: {
      chart: baseChart("iskool360-transaction-volume", "bar"),
      colors: [SERIES[1]],
      plotOptions: { bar: { columnWidth: "40%", borderRadius: 4 } },
      dataLabels: { enabled: false },
      xaxis: {
        categories: monthlyCollections.map((m) => m.month),
        axisBorder: { show: false },
        axisTicks: { show: false },
        labels: axisLabelStyle,
      },
      yaxis: { labels: { formatter: (v) => Math.round(v) } },
      tooltip: { y: { formatter: (v) => `${v} transaction${v === 1 ? "" : "s"}` } },
      grid: baseGrid,
    },
    series: [
      { name: "Transactions", data: monthlyCollections.map((m) => m.transactions || 0) },
    ],
  };

  return (
    <SectionShell
      id="finance"
      title="Finance & Fees"
      subtitle={`Academic year ${academicYear}`}
      accentColor={ACCENT}
    >
      <div className="k360-grid k360-cols-2-4 k360-mb">
        <KpiStatTile
          title="Expected"
          value={formatCurrency(fee.expected)}
          caption="Total billed this year"
          icon={<AiOutlineWallet size={17} />}
          chipClass="bg-sky-50 text-sky-700"
          accentColor={ACCENT}
        />
        <KpiStatTile
          title="Collected"
          value={formatCurrency(fee.collected)}
          caption={`${collectedPctOfExpected}% of expected`}
          icon={<AiOutlineDollarCircle size={17} />}
          chipClass="bg-emerald-50 text-emerald-600"
          captionClass="text-emerald-600 font-semibold"
          accentColor={ACCENT}
        />
        <KpiStatTile
          title="Outstanding"
          value={formatCurrency(fee.remaining)}
          caption="Yet to be collected"
          icon={<AiOutlineExclamationCircle size={17} />}
          chipClass="bg-rose-50 text-rose-600"
          captionClass="text-rose-500 font-semibold"
          accentColor={ACCENT}
        />
        <KpiStatTile
          title="Discounts"
          value={formatCurrency(fee.discount)}
          caption={`Across ${fee.studentFees || 0} student fee records`}
          icon={<AiOutlineTags size={17} />}
          chipClass="bg-amber-50 text-amber-600"
          accentColor={ACCENT}
        />
      </div>

      <div className="k360-grid k360-cols-2 k360-mb">
        <Card accentColor={ACCENT}>
          <CardTitle title="Collection Rate" subtitle="Collected vs expected" />
          <Chart
            options={collectionGauge.options}
            series={collectionGauge.series}
            type="radialBar"
            height={230}
          />
        </Card>

        <Card accentColor={ACCENT}>
          <CardTitle
            title="Monthly Collections"
            subtitle={`Approved payments · ${new Date().getFullYear()}`}
          />
          {hasCollections ? (
            <Chart
              options={collectionsArea.options}
              series={collectionsArea.series}
              type="area"
              height={260}
            />
          ) : (
            <Empty description="No approved payments yet" className="py-12" />
          )}
        </Card>

        <Card accentColor={ACCENT}>
          <CardTitle
            title="Transaction Volume"
            subtitle={`Avg. ${formatCurrency(avgTransactionValue)} per transaction`}
          />
          {hasCollections ? (
            <Chart
              options={transactionVolumeBar.options}
              series={transactionVolumeBar.series}
              type="bar"
              height={260}
            />
          ) : (
            <Empty description="No transactions yet" className="py-12" />
          )}
        </Card>

        <Card accentColor={ACCENT}>
          <CardTitle
            title="Fee Status Split"
            subtitle="Student fee records by status"
          />
          {hasFeeStatusData ? (
            <Chart
              options={feeStatusDonut.options}
              series={feeStatusDonut.series}
              type="donut"
              height={260}
            />
          ) : (
            <Empty description="No fee records yet" className="py-12" />
          )}
        </Card>
      </div>

      <div className="k360-grid k360-cols-2">
        <LinkRow
          icon={<AiOutlineFileDone size={18} />}
          iconBg="#ecfdf5"
          iconColor="#10b981"
          label="Payment approvals"
          caption="Transactions waiting for review"
          right={
            <Badge
              count={overview.pendingTransactions}
              showZero
              style={{
                backgroundColor:
                  overview.pendingTransactions > 0 ? "#f43f5e" : "#cbd5e1",
              }}
            />
          }
          onClick={() => onNavigate("/dashboard/admin/menu/fee/approve")}
        />
        <LinkRow
          icon={<AiOutlinePercentage size={18} />}
          iconBg="#fffbeb"
          iconColor="#f59e0b"
          label="Discount approvals"
          caption="Discount requests pending"
          right={
            <Badge
              count={overview.pendingDiscounts}
              showZero
              style={{
                backgroundColor:
                  overview.pendingDiscounts > 0 ? "#f43f5e" : "#cbd5e1",
              }}
            />
          }
          onClick={() =>
            onNavigate("/dashboard/admin/menu/fee/approve-discount")
          }
        />
      </div>
    </SectionShell>
  );
};

export default FinanceSection;
